import {
  FAIL_REASON_LABELS,
  FailReason,
  JOB_STATUS_LABELS,
  NF_STATUS_LABELS,
  NfStatus,
  ORDER_FLOW,
  ORDER_STATUS_LABELS,
  Order,
  POSTED_METHODS,
  SHIPPING_METHOD_LABELS,
  ShippingMethod,
  formatBRL,
  formatDate,
  formatIsoDate,
  depositText,
} from '@deizi/shared';
import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { Field } from '../../components/Form';
import { Icon } from '../../components/Icon';
import { Modal, Sheet } from '../../components/Overlays';
import { ErrorBox, Skeleton } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { uploadFile } from '../../lib/files-api';
import { errorMessage } from '../../lib/messages';
import { formatHm, parseBrDate } from '../../lib/numbers';
import { useInvalidateOrders, useOrder, useOrderMessages } from '../../lib/orders-api';
import { useIsPhone } from '../../lib/use-media';
import { CloseDialog, EndDialog, ShipDialog, StatusBadge, copyToClipboard } from './common';

const h1 = (h: number) => h.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
const pct = (bps: number) =>
  `${(bps / 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

function rowValue(unit: 'g' | 'h' | 'centavos', v: number) {
  return unit === 'g'
    ? `${Math.round(v).toLocaleString('pt-BR')} g`
    : unit === 'h'
      ? `${h1(v)} h`
      : formatBRL(v);
}

function ProfitCard({ order }: { order: Order }) {
  const p = order.profit;
  const diff = p.estimatedProfitCentavos
    ? (p.realProfitCentavos - p.estimatedProfitCentavos) / Math.abs(p.estimatedProfitCentavos)
    : 0;
  const realLabel = order.ended
    ? `Real · antes do ${order.ended.kind === 'cancelled' ? 'cancelamento' : 'retorno'}`
    : p.final
      ? 'Real · final'
      : 'Real · até agora';
  return (
    <section className="card col" style={{ gap: 12 }}>
      <div className="row between">
        <h2>Lucro: estimado vs. real</h2>
        <span className={diff < -0.1 ? 'badge warn' : 'badge'}>
          {diff >= 0 ? '+' : '−'}
          {h1(Math.abs(diff * 100))}% do estimado
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="card sunk" style={{ padding: 12, borderRadius: 12 }}>
          <p className="xs muted">Estimado no {order.quote ? 'orçamento' : 'pedido'}</p>
          <p className="fr tab" style={{ fontSize: 24 }}>
            {formatBRL(p.estimatedProfitCentavos)}
          </p>
          <p className="xs muted">{pct(p.estimatedMarginBps)} de margem</p>
        </div>
        <div className="card sunk" style={{ padding: 12, borderRadius: 12 }}>
          <p className="xs muted">{realLabel}</p>
          <p
            className="fr tab"
            style={{ fontSize: 24, color: p.realProfitCentavos < 0 ? 'var(--err)' : undefined }}
          >
            {formatBRL(p.realProfitCentavos)}
          </p>
          <p className="xs muted">
            {pct(p.realMarginBps)} de margem
            {order.closedAt
              ? ` · fechado em ${formatDate(order.closedAt)}`
              : p.final
                ? ''
                : ' · vira final ao fechar'}
          </p>
        </div>
      </div>
      {order.ended ? (
        <div className={order.ended.lossCentavos ? 'alert err' : 'alert ok'}>
          <span className="sm">
            {order.ended.lossCentavos
              ? `Perda no ${order.ended.kind === 'cancelled' ? 'cancelamento' : 'retorno'}: − ${formatBRL(order.ended.lossCentavos)}`
              : 'Sem perda'}{' '}
            · reembolso {formatBRL(order.ended.refundCentavos)} ·{' '}
            {order.ended.backToStock ? 'voltou ao estoque' : 'não voltou ao estoque'}
            {order.ended.note ? ` · ${order.ended.note}` : ''}
          </span>
        </div>
      ) : null}
      <div className="gt">
        <div className="gtr head" style={{ gridTemplateColumns: '1.4fr 1fr 1fr 1fr' }}>
          <span />
          <span className="num">Estimado</span>
          <span className="num">Real</span>
          <span className="num">Diferença</span>
        </div>
        {p.rows.map((r) => {
          const d = r.real - r.estimated;
          return (
            <div
              key={r.key}
              className={r.key === 'cost' ? 'gtr foot' : 'gtr'}
              style={{ gridTemplateColumns: '1.4fr 1fr 1fr 1fr' }}
            >
              <span>{r.label}</span>
              <span className="num">{rowValue(r.unit, r.estimated)}</span>
              <span className="num">{rowValue(r.unit, r.real)}</span>
              <span className="num" style={{ color: d > 0 ? 'var(--warn)' : undefined }}>
                {d === 0 ? '—' : `${d > 0 ? '+' : '−'} ${rowValue(r.unit, Math.abs(d))}`}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ShippingDialog({ order, onClose }: { order: Order; onClose: () => void }) {
  const toast = useToast();
  const invalidate = useInvalidateOrders();
  const [method, setMethod] = useState<string>(order.shippingMethod ?? '');
  const [shipBy, setShipBy] = useState(order.shipBy ? formatIsoDate(order.shipBy) : '');
  const [tracking, setTracking] = useState(order.trackingCode ?? '');
  const [nf, setNf] = useState(order.nfStatus);
  const [nfNumber, setNfNumber] = useState(order.nfNumber ?? '');
  const [nfKey, setNfKey] = useState(order.nfKey ?? '');
  const shipIso = shipBy.trim() ? parseBrDate(shipBy) : null;
  const keyDigits = nfKey.replace(/\D/g, '');
  const save = useMutation({
    mutationFn: () =>
      api.patch(`/orders/${order.id}`, Order, {
        shippingMethod: method ? ShippingMethod.parse(method) : null,
        shipBy: shipIso,
        trackingCode: tracking.trim() || null,
        nfStatus: nf,
        nfNumber: nfNumber.trim() || null,
        nfKey: keyDigits ? keyDigits : null,
      }),
    onSuccess: () => {
      invalidate();
      toast('Envio e nota fiscal atualizados');
      onClose();
    },
  });
  const attach = useMutation({
    mutationFn: async (v: { file: File; role: 'label' | 'nf_xml' | 'nf_pdf' }) => {
      const stored = await uploadFile(v.file, 'document');
      return api.post(`/orders/${order.id}/files`, Order, { fileId: stored.id, role: v.role });
    },
    onSuccess: (_o, v) => {
      invalidate();
      toast(
        v.role === 'label'
          ? 'Etiqueta anexada'
          : v.role === 'nf_xml'
            ? 'XML da nota anexado'
            : 'PDF da nota anexado',
      );
    },
    onError: (err) => toast(errorMessage(err)),
  });
  const input = useRef<HTMLInputElement>(null);
  const [role, setRole] = useState<'label' | 'nf_xml' | 'nf_pdf'>('label');
  const pick = (r: typeof role) => {
    setRole(r);
    input.current?.click();
  };
  const posted = method !== '' && POSTED_METHODS.includes(ShippingMethod.parse(method));
  const keyBad = nf === 'issued' && keyDigits.length > 0 && keyDigits.length !== 44;
  return (
    <Modal
      title="Envio e nota fiscal"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn p"
            disabled={keyBad || (shipBy.trim() !== '' && !shipIso) || save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar
          </button>
        </>
      }
    >
      <input
        ref={input}
        type="file"
        hidden
        accept=".pdf,.xml,image/*"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) attach.mutate({ file: f, role });
          e.target.value = '';
        }}
      />
      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr' }}>
        <Field id="sd-m" label="Método">
          <select
            id="sd-m"
            className="input"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
          >
            <option value="">Escolher</option>
            {ShippingMethod.options.map((m) => (
              <option key={m} value={m}>
                {SHIPPING_METHOD_LABELS[m]}
              </option>
            ))}
          </select>
        </Field>
        <Field
          id="sd-sb"
          label="Prazo de envio"
          error={shipBy.trim() && !shipIso ? 'Use DD/MM/AAAA.' : null}
        >
          <input
            id="sd-sb"
            className="input"
            inputMode="numeric"
            value={shipBy}
            onChange={(e) => setShipBy(e.target.value)}
          />
        </Field>
      </div>
      {posted ? (
        <>
          <Field id="sd-tr" label="Rastreio">
            <input
              id="sd-tr"
              className="input"
              placeholder="BR…"
              value={tracking}
              onChange={(e) => setTracking(e.target.value)}
            />
          </Field>
          <button
            type="button"
            className="btn sm"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => pick('label')}
          >
            {order.labelFile ? `${order.labelFile.name} · trocar` : 'Anexar etiqueta PDF'}
          </button>
        </>
      ) : null}
      <Field id="sd-nf" label="Nota fiscal">
        <select
          id="sd-nf"
          className="input"
          value={nf}
          onChange={(e) => setNf(NfStatus.parse(e.target.value))}
        >
          {NfStatus.options.map((s) => (
            <option key={s} value={s}>
              {NF_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </Field>
      {nf === 'issued' ? (
        <>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 2fr' }}>
            <Field id="sd-nn" label="Número">
              <input
                id="sd-nn"
                className="input"
                value={nfNumber}
                onChange={(e) => setNfNumber(e.target.value)}
              />
            </Field>
            <Field
              id="sd-nk"
              label="Chave de acesso (44 dígitos)"
              error={keyBad ? `A chave tem 44 dígitos. Você digitou ${keyDigits.length}.` : null}
              help={`${keyDigits.length} de 44 dígitos · pode colar com espaços.`}
            >
              <input
                id="sd-nk"
                className="input"
                inputMode="numeric"
                value={nfKey}
                onChange={(e) => setNfKey(e.target.value)}
              />
            </Field>
          </div>
          <div className="row" style={{ gap: 8 }}>
            <button type="button" className="btn sm" onClick={() => pick('nf_xml')}>
              {order.nfXmlFile ? 'XML anexado · trocar' : 'Anexar XML'}
            </button>
            <button type="button" className="btn sm" onClick={() => pick('nf_pdf')}>
              {order.nfPdfFile ? 'PDF anexado · trocar' : 'Anexar PDF (DANFE)'}
            </button>
          </div>
        </>
      ) : null}
      {save.isError ? <p className="help err">{errorMessage(save.error)}</p> : null}
    </Modal>
  );
}

/** Pedido (boards Pedido-Detalhe, Pedido-Celular). */
export function OrderPage() {
  const { id } = useParams();
  const order = useOrder(id);
  const messages = useOrderMessages(id ?? '');
  const phone = useIsPhone();
  const [actions, setActions] = useState(false);
  const jobCols = phone
    ? { gridTemplateColumns: '64px 1fr auto' }
    : { gridTemplateColumns: '90px 1.5fr 80px 80px 1.4fr', minWidth: 560 };
  const toast = useToast();
  const invalidate = useInvalidateOrders();
  const [dialog, setDialog] = useState<'ship' | 'close' | 'cancel' | 'return' | 'shipping' | null>(
    null,
  );
  const act = useMutation({
    mutationFn: (path: string) => api.post(`/orders/${id}/${path}`, Order),
    onSuccess: () => invalidate(),
    onError: (err) => toast(errorMessage(err)),
  });
  const check = useMutation({
    mutationFn: (v: { key: string; done: boolean }) =>
      api.post(`/orders/${id}/checklist/${v.key}`, Order, { done: v.done }),
    onSuccess: (o, v) => {
      invalidate();
      if (v.key === 'packed' && v.done)
        toast(
          o.packagingKit
            ? `Embalado. O kit ${o.packagingKit.name} saiu do estoque de insumos.`
            : 'Embalado.',
        );
    },
    onError: (err) => toast(errorMessage(err)),
  });

  if (order.isPending) return <Skeleton rows={3} height={140} />;
  if (order.isError)
    return (
      <ErrorBox title="Não foi possível carregar o pedido" onRetry={() => void order.refetch()} />
    );
  const o = order.data;
  const ended = !!o.ended;
  const missing = o.items.reduce((n, i) => n + i.missingUnits, 0);
  const steps = [...ORDER_FLOW, ...(o.ended ? [o.status] : [])];
  const at = ORDER_FLOW.indexOf(o.status);
  const copy = (text: string | undefined) =>
    void copyToClipboard(text ?? '').then((ok) =>
      toast(ok ? 'Copiado. É só colar.' : 'Não deu para copiar.'),
    );

  return (
    <div className="col" style={{ gap: 18 }}>
      <div className="pagehead">
        <div>
          <div className="crumb">
            <Link to="/pedidos">Pedidos</Link> / {o.number}
            {o.quote ? (
              <>
                {' '}
                · de <Link to={`/orcamentos/${o.quote.id}`}>{o.quote.number}</Link>
              </>
            ) : null}
          </div>
          <div className="row wrap" style={{ gap: 10 }}>
            <h1 style={phone ? { fontSize: 22 } : undefined}>{o.itemsSummary}</h1>
            <StatusBadge status={o.status} />
            {o.quote?.deposit ? (
              <span className={o.quote.deposit.paidAt ? 'badge ok' : 'badge warn'}>
                Sinal {depositText(o.quote.deposit)}
              </span>
            ) : null}
          </div>
          <p className="sm muted">
            {o.customer?.name ?? 'sem cliente'} · {o.channel.name}
            {o.paymentMethod ? ` · ${o.paymentMethod}` : ''} · vendido em {formatIsoDate(o.soldOn)}
          </p>
        </div>
        <div className="row wrap" style={{ gap: 8 }}>
          {o.status === 'new' ? (
            <button
              type="button"
              className="btn p"
              disabled={act.isPending}
              onClick={() => act.mutate('confirm')}
            >
              Confirmar pedido
            </button>
          ) : null}
          {o.status === 'in_production' || o.status === 'to_pack' ? (
            <button
              type="button"
              className={o.status === 'to_pack' ? 'btn p' : 'btn'}
              onClick={() => setDialog('ship')}
            >
              Marcar enviado
            </button>
          ) : null}
          {!phone && (o.status === 'shipped' || o.status === 'closed') ? (
            <button type="button" className="btn" onClick={() => setDialog('return')}>
              Devolução
            </button>
          ) : null}
          {!phone && !ended && o.status !== 'closed' ? (
            <button type="button" className="btn" onClick={() => setDialog('cancel')}>
              Cancelar
            </button>
          ) : null}
          {o.status === 'to_pack' || o.status === 'shipped' ? (
            <button
              type="button"
              className={o.status === 'shipped' ? 'btn p' : 'btn'}
              onClick={() => setDialog('close')}
            >
              Fechar pedido
            </button>
          ) : null}
          {phone ? (
            <button
              type="button"
              className="btn icon"
              aria-label="Ações do pedido"
              onClick={() => setActions(true)}
            >
              <Icon name="dots" />
            </button>
          ) : null}
        </div>
      </div>
      {actions ? (
        <Sheet label="Ações do pedido" onClose={() => setActions(false)}>
          <h2>Ações do pedido</h2>
          <div className="col" style={{ gap: 0 }}>
            <button
              type="button"
              className="listrow"
              onClick={() => {
                copy(messages.data?.inProduction);
                setActions(false);
              }}
            >
              Copiar “em produção”
            </button>
            <button
              type="button"
              className="listrow"
              onClick={() => {
                copy(messages.data?.shipped);
                setActions(false);
              }}
            >
              Copiar “enviado”
            </button>
            {o.status === 'shipped' || o.status === 'closed' ? (
              <button
                type="button"
                className="listrow"
                onClick={() => {
                  setActions(false);
                  setDialog('return');
                }}
              >
                Registrar devolução
              </button>
            ) : null}
            {!ended && o.status !== 'closed' ? (
              <button
                type="button"
                className="listrow"
                style={{ color: 'var(--err)' }}
                onClick={() => {
                  setActions(false);
                  setDialog('cancel');
                }}
              >
                Cancelar pedido
              </button>
            ) : null}
            <button
              type="button"
              className="listrow"
              onClick={() => {
                void copyToClipboard(window.location.href).then((ok) =>
                  toast(
                    ok
                      ? 'Link copiado. Abra no computador para ver tudo lado a lado.'
                      : 'Não deu para copiar.',
                  ),
                );
                setActions(false);
              }}
            >
              Abrir no computador
            </button>
          </div>
        </Sheet>
      ) : null}

      <div className="row wrap" style={{ gap: 6 }} aria-label="Andamento">
        {steps.map((s, i) => (
          <span
            key={s}
            className={
              s === o.status
                ? ended
                  ? 'badge err'
                  : 'badge info'
                : i < at || (ended && i < 3)
                  ? 'badge ok'
                  : 'badge'
            }
          >
            {ORDER_STATUS_LABELS[s]}
          </span>
        ))}
      </div>
      {o.late ? (
        <div className="alert err">
          <span className="sm">
            <b>Atrasado.</b> O prazo de envio era {o.shipBy ? formatIsoDate(o.shipBy) : ''}. Embale
            e poste hoje.
          </span>
        </div>
      ) : null}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'flex-start' }}>
        <div className="col" style={{ flex: '2 1 520px', minWidth: 0, gap: 18 }}>
          <ProfitCard order={o} />
          <section className="card flat">
            <div className="row between" style={{ padding: '16px 18px 6px' }}>
              <h2>Produção</h2>
              <span className="row" style={{ gap: 8 }}>
                {missing > 0 ? (
                  <button
                    type="button"
                    className="btn sm p"
                    disabled={act.isPending}
                    onClick={() => act.mutate('reprint')}
                  >
                    Imprimir o que falta ({missing})
                  </button>
                ) : null}
                <Link className="btn sm g" to="/fila">
                  Abrir fila
                </Link>
              </span>
            </div>
            <div className="gt" style={{ overflowX: 'auto' }}>
              <div className="gtr head" style={jobCols}>
                <span>Impressão</span>
                <span>Placa</span>
                {phone ? null : (
                  <>
                    <span className="num">Tempo</span>
                    <span className="num">Gramas</span>
                  </>
                )}
                <span>Resultado</span>
              </div>
              {o.jobs.map((j) => (
                <div key={j.id} className="gtr" style={jobCols}>
                  <Link className="tab" to={`/fila?job=${j.id}`}>
                    {j.number}
                  </Link>
                  <span className="sm">
                    {j.units} un. · {j.material ?? '—'}
                    {j.redoOf ? ` · refeita de ${j.redoOf}` : ''}
                    {phone ? (
                      <span className="xs muted" style={{ display: 'block' }}>
                        {formatHm(j.actualSeconds ?? j.estSeconds)} ·{' '}
                        {Math.round(j.actualGrams ?? j.estGrams)} g
                      </span>
                    ) : null}
                  </span>
                  {phone ? null : (
                    <>
                      <span className="num">{formatHm(j.actualSeconds ?? j.estSeconds)}</span>
                      <span className="num">{Math.round(j.actualGrams ?? j.estGrams)} g</span>
                    </>
                  )}
                  <span>
                    {j.status === 'failed' ? (
                      <span className="badge err">
                        Falhou {j.failPct ?? 0}%
                        {j.failReason
                          ? ` · ${FAIL_REASON_LABELS[FailReason.parse(j.failReason)]}`
                          : ''}
                      </span>
                    ) : (
                      <span
                        className={
                          j.status === 'done'
                            ? 'badge ok'
                            : j.status === 'printing'
                              ? 'badge info'
                              : 'badge'
                        }
                      >
                        {JOB_STATUS_LABELS[j.status]}
                      </span>
                    )}
                  </span>
                </div>
              ))}
              {o.jobs.length === 0 ? (
                <div className="gtr" style={{ gridTemplateColumns: '1fr' }}>
                  <span className="sm muted">
                    {o.items.some((i) => i.fromStockUnits)
                      ? 'Tudo saiu da pronta entrega.'
                      : o.status === 'new'
                        ? 'Confirme o pedido para mandar para a fila.'
                        : 'Sem impressões.'}
                  </span>
                </div>
              ) : null}
            </div>
          </section>
          <section className="card col" style={{ gap: 4 }}>
            <div className="row between">
              <h2>Envio e nota fiscal</h2>
              <button type="button" className="btn sm" onClick={() => setDialog('shipping')}>
                Editar
              </button>
            </div>
            <div className="kv sm">
              <span className="muted">Método</span>
              <span className="v">
                {o.shippingMethod ? SHIPPING_METHOD_LABELS[o.shippingMethod] : '—'}
              </span>
            </div>
            <div className="kv sm">
              <span className="muted">Prazo de envio</span>
              <span className="v" style={{ color: o.late ? 'var(--err)' : undefined }}>
                {o.shipBy ? formatIsoDate(o.shipBy) : '—'}
                {o.late ? ' · atrasado' : ''}
              </span>
            </div>
            <div className="kv sm">
              <span className="muted">Rastreio</span>
              <span className="v">
                {o.trackingCode ??
                  (o.shippingMethod && POSTED_METHODS.includes(o.shippingMethod)
                    ? 'aguardando postagem'
                    : 'não se aplica')}
              </span>
            </div>
            <div className="kv sm">
              <span className="muted">Etiqueta</span>
              <span className="v">
                {o.labelFile ? (
                  <a href={`${o.labelFile.url}?download=1`}>{o.labelFile.name}</a>
                ) : o.shippingMethod && POSTED_METHODS.includes(o.shippingMethod) ? (
                  'falta anexar'
                ) : (
                  'não precisa'
                )}
              </span>
            </div>
            <div className="kv sm">
              <span className="muted">Nota fiscal</span>
              <span
                className={
                  o.nfStatus === 'issued'
                    ? 'badge ok'
                    : o.nfStatus === 'pending'
                      ? 'badge warn'
                      : 'badge'
                }
              >
                {NF_STATUS_LABELS[o.nfStatus]}
              </span>
            </div>
            {o.nfStatus === 'issued' ? (
              <>
                <div className="kv sm">
                  <span className="muted">Número</span>
                  <span className="v">{o.nfNumber ?? '—'}</span>
                </div>
                <div className="kv sm">
                  <span className="muted">Chave de acesso</span>
                  <span className="v xs">
                    {o.nfKey ? o.nfKey.replace(/(\d{4})(?=\d)/g, '$1 ') : '—'}
                  </span>
                </div>
                <div className="kv sm">
                  <span className="muted">Arquivos</span>
                  <span className="v">
                    {[o.nfXmlFile ? 'XML' : null, o.nfPdfFile ? 'PDF' : null]
                      .filter(Boolean)
                      .join(' + ') || 'nenhum arquivo'}
                  </span>
                </div>
              </>
            ) : null}
          </section>
        </div>
        <aside className="col" style={{ flex: '1 1 300px', minWidth: 0, gap: 18 }}>
          <section className="card col" style={{ gap: 4 }}>
            <div className="row between">
              <h2 style={{ fontSize: 18 }}>
                {o.shippingMethod === 'hand' ? 'Checklist de entrega' : 'Checklist de envio'}
              </h2>
              <span className="sm muted">{o.checklist.filter((c) => c.done).length} de 4</span>
            </div>
            {o.checklist.map((c) => (
              <button
                key={c.key}
                type="button"
                className="check sm"
                aria-pressed={c.done}
                disabled={check.isPending || ended}
                onClick={() =>
                  c.key === 'delivered' && !c.done && o.status !== 'shipped'
                    ? setDialog('ship')
                    : check.mutate({ key: c.key, done: !c.done })
                }
              >
                <span className={c.done ? 'box on' : 'box'}>{c.done ? '✓' : null}</span>
                <span className="grow" style={{ textAlign: 'left' }}>
                  {c.label}
                  <span className="xs muted" style={{ display: 'block' }}>
                    {c.done && c.doneAt ? `${formatDate(c.doneAt)} · ${c.doneBy}` : 'pendente'}
                  </span>
                </span>
              </button>
            ))}
          </section>
          <section className="card col" style={{ gap: 4 }}>
            <h2 style={{ fontSize: 18 }}>Itens</h2>
            {o.items.map((i) => (
              <div key={i.id} className="kv sm">
                <span>
                  {i.description} ×{i.quantity}
                  <span className="xs muted" style={{ display: 'block' }}>
                    {[
                      i.fromStockUnits ? `${i.fromStockUnits} da pronta entrega` : null,
                      i.producedUnits ? `${i.producedUnits} impressas` : null,
                      i.queuedUnits ? `${i.queuedUnits} na fila` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                <span className="v">{formatBRL(i.unitPriceCentavos * i.quantity)}</span>
              </div>
            ))}
            {o.packagingKit ? (
              <div className="kv sm">
                <span>Kit {o.packagingKit.name}</span>
                <span className="v">{o.kitQuantity}</span>
              </div>
            ) : null}
            <div className="kv sm">
              <span className="muted">Nº no marketplace</span>
              <span className="v">{o.marketplaceOrderNo ?? 'venda direta'}</span>
            </div>
          </section>
          <section className="card col" style={{ gap: 8 }}>
            <h2 style={{ fontSize: 18 }}>Mensagens</h2>
            <button
              type="button"
              className="btn sm"
              onClick={() => copy(messages.data?.inProduction)}
            >
              Copiar “em produção”
            </button>
            <button type="button" className="btn sm" onClick={() => copy(messages.data?.shipped)}>
              Copiar “enviado”
            </button>
          </section>
          <section className="card col" style={{ gap: 4 }}>
            <h2 style={{ fontSize: 18 }}>Integração futura</h2>
            <p className="xs muted">
              Nº no marketplace e ID do anúncio ficam aqui, preenchidos à mão por enquanto. A nota
              fiscal fica no card Envio e nota fiscal.
            </p>
          </section>
        </aside>
      </div>
      {dialog === 'ship' ? <ShipDialog order={o} onClose={() => setDialog(null)} /> : null}
      {dialog === 'close' ? <CloseDialog order={o} onClose={() => setDialog(null)} /> : null}
      {dialog === 'cancel' || dialog === 'return' ? (
        <EndDialog order={o} kind={dialog} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'shipping' ? <ShippingDialog order={o} onClose={() => setDialog(null)} /> : null}
    </div>
  );
}
